/**
 * batch-39 bibliotheek (FO-333) — upload met automatische naam, type en tags.
 *
 * Pure helpers + Zod-schemas voor de metadata-voorstellen die het gedeelde
 * upload-formulier (bibliotheek + medewerker-/relatie-/voertuigdossier) toont
 * vóór het opslaan:
 *
 *  - `schoonBestandsnaam`      — bestandsnaam → leesbare naam (unit-getest).
 *  - `DOCUMENTTYPE_SLEUTELWOORDEN` — beheerbare sleutelwoorden-map voor de
 *    type-heuristiek (nieuwe DocumentType-waarde? de Record dwingt een keuze af).
 *  - `detecteerDocumenttype`   — heuristiek + context-voorrang; ONZEKER = null
 *    (nooit een stille gok; het formulier toont dan een "kies type"-nudge).
 *  - Weergavenaam-patronen (`{type} - {betrokkene} - {datum}`) — een APARTE,
 *    beheerbare set naast de bestandsnaam-patronen (batch-11 F3 / FO-152):
 *    weergavenamen behouden literale tekst, export-bestandsnamen niet.
 *  - Mappad → label-voorstellen (FO-145: labels zijn de virtuele mappen).
 *
 * De server-action `stelDocumentMetadataVoor` (apps/api) combineert deze
 * helpers met de FO-145-label-engine in preview-modus en de optionele korte
 * AI-classificatie.
 */

import { z } from 'zod'
import {
  BIBLIOTHEEK_DOCUMENT_TYPE_LABELS,
  DOCUMENT_CATEGORIE,
  DOCUMENT_SCOPE,
  DOCUMENT_TYPE,
  type DocumentType,
} from './documenten'
import type { MedewerkerVoorstelDTO } from './medewerker-match'

// ─── Naam-schoning ────────────────────────────────────────────────────────────

/** Nederlandse tussenvoegsels die in een titel-casing klein blijven. */
const TUSSENVOEGSELS = new Set([
  'de', 'den', 'der', 'het', 'te', 'ten', 'ter', 'van', 'vd', 'v/d', 'op', 'in',
  "'t", 'aan', 'bij', 'onder', 'over', 'tot', 'uit', 'voor', 'en',
])

/** Herkenbare bestandsextensie (1-5 alfanumerieke tekens na de laatste punt). */
const EXTENSIE_RE = /\.[a-z0-9]{1,5}$/i

/**
 * Schoon een bestandsnaam op tot een leesbaar naam-voorstel (FO-333 §2.1):
 * extensie eraf, underscores/streepjes → spaties (datums zoals `12-05-2026`
 * blijven intact), dubbele spaties weg, nette hoofdletters (tussenvoegsels
 * klein, bestaande hoofdletters zoals "VCA" blijven staan).
 *
 * `arbeidsovereenkomst_jan_de_vries_2026.pdf` → "Arbeidsovereenkomst Jan de Vries 2026".
 */
export function schoonBestandsnaam(bestandsnaam: string): string {
  let naam = bestandsnaam.trim()
  // Extensie strippen (alleen de laatste, en alleen als het op een extensie lijkt).
  naam = naam.replace(EXTENSIE_RE, '')
  // Underscores altijd → spatie; streepjes alleen buiten cijfer-cijfer
  // (datums als 12-05-2026 en reeksen als 2025-2026 blijven staan).
  naam = naam.replace(/_/g, ' ')
  naam = naam.replace(/(?<!\d)-|-(?!\d)/g, ' ')
  // Meerdere spaties/punten opruimen.
  naam = naam.replace(/\s+/g, ' ').trim()
  if (naam.length === 0) return ''

  const woorden = naam.split(' ')
  const uit = woorden.map((woord, i) => {
    // Bevat het woord al een hoofdletter (VCA, NEN3140, McDonald)? Laat staan.
    if (/[A-Z]/.test(woord)) return woord
    // Puur cijfers/datums laten we ongemoeid.
    if (!/[a-z]/.test(woord)) return woord
    // Tussenvoegsels klein houden, behalve als eerste woord.
    if (i > 0 && TUSSENVOEGSELS.has(woord)) return woord
    return woord.charAt(0).toUpperCase() + woord.slice(1)
  })
  return uit.join(' ')
}

// ─── Type-heuristiek (beheerbare sleutelwoorden-map) ──────────────────────────

/**
 * Beheerbare sleutelwoorden-map: bestandsnaam-woorden → documenttype-suggestie.
 * Volledige `Record` over alle typen zodat een nieuwe enum-waarde de typecheck
 * breekt en bewust een (eventueel lege) lijst krijgt. Sleutelwoorden zijn
 * kleine letters; meerwoordige sleutels matchen als frase, éénwoordige als
 * heel woord. Een generiek woord mag bij méérdere typen staan ("contract"):
 * de context-voorrang beslist dan, of het voorstel blijft leeg (onzeker).
 */
export const DOCUMENTTYPE_SLEUTELWOORDEN: Record<DocumentType, readonly string[]> = {
  FACTUUR: ['factuur', 'invoice'],
  INKOOPFACTUUR: ['inkoopfactuur', 'inkoop factuur'],
  CREDITFACTUUR: ['creditfactuur', 'creditnota', 'credit factuur', 'credit nota'],
  WERKBON: ['werkbon'],
  CERTIFICAAT: ['certificaat', 'certificate', 'vca', 'bhv', 'nen 3140', 'nen3140', 'ehbo'],
  DIPLOMA: ['diploma'],
  SKILLMATRIX: ['skillmatrix', 'skilmatrix', 'skill matrix'],
  ARBEIDSOVEREENKOMST: ['arbeidsovereenkomst', 'arbeids overeenkomst', 'contract'],
  BRUIKLEENOVEREENKOMST: ['bruikleenovereenkomst', 'bruikleen'],
  WERKGEVERSVERKLARING: ['werkgeversverklaring'],
  LOONSTROOK: ['loonstrook', 'salarisstrook', 'salarisspecificatie', 'loonspecificatie'],
  JAAROPGAVE: ['jaaropgave', 'jaaropgaaf'],
  DECLARATIEBON: ['declaratie', 'declaratiebon', 'kassabon', 'bonnetje'],
  OFFERTE: ['offerte'],
  KLANTCONTRACT: ['klantcontract', 'onderhoudscontract', 'servicecontract', 'raamovereenkomst', 'contract'],
  VEILIGHEIDSDOCUMENT: ['veiligheid', 'toolbox', 'ri&e', 'rie', 'v&g', 'lmra'],
  OPLEIDINGSDOCUMENT: ['opleiding', 'cursus', 'training'],
  HANDLEIDING: ['handleiding', 'manual', 'instructie', 'gebruiksaanwijzing'],
  TEMPLATE: ['template', 'sjabloon'],
  // Vangnet-type: nooit via een sleutelwoord voorstellen.
  ALGEMEEN_DOCUMENT: [],
  SCHADEFORMULIER: ['schadeformulier', 'schade formulier', 'aanrijdingsformulier'],
  ID_KAART: ['id kaart', 'identiteitskaart', 'idkaart', 'id bewijs', 'identiteitsbewijs'],
  PASPOORT: ['paspoort', 'passport'],
  RIJBEWIJS: ['rijbewijs'],
  BANKPAS: ['bankpas', 'bankkaart'],
  GROENE_KAART: ['groene kaart', 'groenekaart', 'internationaal verzekeringsbewijs'],
  INZETVERKLARING: ['inzetverklaring', 'inzet verklaring'],
  // batch-65 apk-rapport (FO-478) - "apk" als heel woord; "keuring" alleen in
  // frases (een losse "keuring" kan ook een NEN-/gereedschapskeuring zijn).
  APK_RAPPORT: ['apk', 'apk rapport', 'apkrapport', 'apk keuring', 'keuringsrapport', 'keuringsbewijs apk'],
  VERKLARING_PRIVEGEBRUIK: [
    'verklaring geen privegebruik',
    'verklaring geen privégebruik',
    'verklaring privegebruik',
    'verklaring privégebruik',
    'uitsluitend zakelijk gebruik',
  ],
  // batch-68 werkplaatsfacturen (FO-341) - ontstaat automatisch uit de
  // inkoopfactuur; een upload-voorstel zou een tweede kopie maken. Leeg.
  WERKPLAATSFACTUUR: [],
}

/** Context-soorten van het gedeelde upload-formulier. */
// batch-55 (FO-421) — `uniekItem` = uniek middel (gereedschap, telefoon, kar, koffer).
export const UPLOAD_CONTEXT_SOORTEN = ['bibliotheek', 'medewerker', 'relatie', 'voertuig', 'uniekItem'] as const
export type UploadContextSoort = (typeof UPLOAD_CONTEXT_SOORTEN)[number]

/**
 * Context-voorrang (FO-333 §2.2): per dossier-context de typen die daar het
 * meest logisch zijn, in voorkeursvolgorde. Twee rollen:
 *  1. bij een dubbelzinnige sleutelwoord-match ("contract") wint het type dat
 *     bij de context hoort;
 *  2. de UI toont deze typen bovenaan in de type-keuzelijst.
 * Een context kiest nooit zelf een type zonder sleutelwoord-match — dat zou
 * een stille gok zijn.
 */
export const CONTEXT_VOORKEUR_TYPES: Record<UploadContextSoort, readonly DocumentType[]> = {
  bibliotheek: [],
  medewerker: [
    'ARBEIDSOVEREENKOMST',
    'CERTIFICAAT',
    'DIPLOMA',
    'LOONSTROOK',
    'JAAROPGAVE',
    'WERKGEVERSVERKLARING',
    'SKILLMATRIX',
    'OPLEIDINGSDOCUMENT',
    'BRUIKLEENOVEREENKOMST',
    'DECLARATIEBON',
    'ID_KAART',
    'PASPOORT',
    'RIJBEWIJS',
    'BANKPAS',
  ],
  relatie: ['KLANTCONTRACT', 'OFFERTE', 'FACTUUR', 'CREDITFACTUUR', 'WERKBON'],
  // batch-65 apk-rapport (FO-478) - APK-rapport direct na de groene kaart.
  voertuig: [
    'GROENE_KAART',
    'APK_RAPPORT',
    'INZETVERKLARING',
    'SCHADEFORMULIER',
    'VEILIGHEIDSDOCUMENT',
    'HANDLEIDING',
  ],
  // batch-55 (FO-421) — uniek middel: bruikleendocument, handleiding, keuring/certificaat.
  uniekItem: ['BRUIKLEENOVEREENKOMST', 'HANDLEIDING', 'VEILIGHEIDSDOCUMENT', 'CERTIFICAAT', 'ALGEMEEN_DOCUMENT'],
}

/**
 * FO-375 — persoonsgebonden documenttypen. Bij een van deze typen ZONDER
 * medewerker-koppeling toont de bibliotheek-batch-upload een ZACHTE
 * waarschuwing ("geen medewerker gekoppeld", spec §6.5) - geen blokkade, er
 * bestaan legitieme sjablonen. Spiegelt de medewerker-dossier-typen; los van de
 * picker-voorkeur zodat een volgorde-wijziging de waarschuwing niet raakt.
 */
export const PERSOONSGEBONDEN_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>([
  'ARBEIDSOVEREENKOMST',
  'LOONSTROOK',
  'JAAROPGAVE',
  'WERKGEVERSVERKLARING',
  'CERTIFICAAT',
  'DIPLOMA',
  'SKILLMATRIX',
  'BRUIKLEENOVEREENKOMST',
  'OPLEIDINGSDOCUMENT',
  'DECLARATIEBON',
  'ID_KAART',
  'PASPOORT',
  'RIJBEWIJS',
  'BANKPAS',
])

/** Resultaat van de type-heuristiek. `type === null` betekent: onzeker. */
export interface DocumenttypeDetectie {
  type: DocumentType | null
  /** Hoe het voorstel tot stand kwam (null bij onzeker). */
  bron: 'sleutelwoord' | 'context' | null
  /** Alle sleutelwoord-matches (voor de UI-nudge bij dubbelzinnigheid). */
  kandidaten: DocumentType[]
}

/** Normaliseer een bestandsnaam voor sleutelwoord-matching. */
function normaliseerVoorMatch(bestandsnaam: string): string {
  return bestandsnaam
    .toLowerCase()
    .replace(EXTENSIE_RE, '')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Escape regex-metatekens in een sleutelwoord. */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Detecteer het documenttype uit de bestandsnaam (sleutelwoorden-map) met
 * context-voorrang. Nooit een stille gok: levert de heuristiek geen eenduidig
 * type op, dan is het resultaat `null` (het formulier toont de nudge).
 */
export function detecteerDocumenttype(
  bestandsnaam: string,
  contextSoort: UploadContextSoort = 'bibliotheek',
): DocumenttypeDetectie {
  const genormaliseerd = normaliseerVoorMatch(bestandsnaam)
  if (genormaliseerd.length === 0) return { type: null, bron: null, kandidaten: [] }

  // Verzamel per type de langste sleutelwoord-match (specifieker wint later).
  const matchLengte = new Map<DocumentType, number>()
  for (const type of DOCUMENT_TYPE) {
    for (const woord of DOCUMENTTYPE_SLEUTELWOORDEN[type]) {
      // Heel-woord/frase-match op de genormaliseerde naam ("rie" matcht niet
      // in "industrie"); &-tekens en spaties zitten in de frase zelf.
      const re = new RegExp(`(?:^|\\s)${escapeRegex(woord)}(?:\\s|$)`)
      if (re.test(genormaliseerd)) {
        const huidige = matchLengte.get(type) ?? 0
        if (woord.length > huidige) matchLengte.set(type, woord.length)
      }
    }
  }

  const kandidaten = [...matchLengte.keys()]
  if (kandidaten.length === 0) return { type: null, bron: null, kandidaten: [] }
  if (kandidaten.length === 1) {
    return { type: kandidaten[0], bron: 'sleutelwoord', kandidaten }
  }

  // Meerdere matches: het strikt langste (meest specifieke) sleutelwoord wint
  // ("inkoopfactuur" boven "factuur").
  const langste = Math.max(...matchLengte.values())
  const langsteTypes = kandidaten.filter((t) => matchLengte.get(t) === langste)
  if (langsteTypes.length === 1) {
    return { type: langsteTypes[0], bron: 'sleutelwoord', kandidaten }
  }

  // Nog steeds gelijkspel ("contract" bij arbeids- én klantcontract):
  // context-voorrang beslist. Meerdere context-kandidaten → de eerste in de
  // voorkeurslijst (die volgorde is een bewuste keuze, geen gok).
  const voorkeur = CONTEXT_VOORKEUR_TYPES[contextSoort]
  const inContext = voorkeur.filter((t) => langsteTypes.includes(t))
  if (inContext.length > 0) {
    return { type: inContext[0], bron: 'context', kandidaten }
  }

  // Dubbelzinnig zonder context-uitsluitsel: onzeker (nudge in de UI).
  return { type: null, bron: null, kandidaten }
}

// ─── Weergavenaam-patronen (aparte set naast FO-152/batch-11 F3) ──────────────

/** Tokens voor weergavenaam-patronen. Syntax: `{token}` (literale tekst blijft). */
export const WEERGAVENAAM_TOKENS = ['type', 'betrokkene', 'datum', 'bestandsnaam'] as const
export type WeergavenaamToken = (typeof WEERGAVENAAM_TOKENS)[number]

export const WEERGAVENAAM_TOKEN_LABELS: Record<WeergavenaamToken, string> = {
  type: 'Documenttype (bv. "Certificaat")',
  betrokkene: 'Betrokkene (medewerker, relatie of kenteken uit de dossier-context)',
  datum: 'Datum van vandaag (dd-mm-jjjj)',
  bestandsnaam: 'Opgeschoonde bestandsnaam',
}

/** Default-patroon per documenttype (FO-333 §2.1: `{Type} - {betrokkene} - {datum}`). */
export const WEERGAVENAAM_DEFAULT_PATROON = '{type} - {betrokkene} - {datum}'

export const WEERGAVENAAM_DEFAULT_PATRONEN: Record<DocumentType, string> = Object.fromEntries(
  DOCUMENT_TYPE.map((t) => [t, WEERGAVENAAM_DEFAULT_PATROON]),
) as Record<DocumentType, string>

const WEERGAVENAAM_TOKEN_RE = /\{([a-z]+)\}/g

/**
 * Pas een weergavenaam-patroon toe. Bekende tokens worden vervangen (lege
 * waarde = token vervalt); ` - `-segmenten zonder inhoud verdwijnen zodat een
 * ontbrekende betrokkene geen "Certificaat -  - 12-05-2026" oplevert. Een
 * onbekende token blijft letterlijk staan (zichtbaar voor de beheerder).
 */
export function pasWeergavenaamPatroonToe(
  patroon: string,
  tokens: Partial<Record<WeergavenaamToken, string | null | undefined>>,
): string {
  const vervangen = patroon.replace(WEERGAVENAAM_TOKEN_RE, (heel, naam: string) => {
    if ((WEERGAVENAAM_TOKENS as readonly string[]).includes(naam)) {
      return (tokens[naam as WeergavenaamToken] ?? '').trim()
    }
    return heel
  })
  return vervangen
    .split(' - ')
    .map((deel) => deel.trim())
    .filter((deel) => deel.length > 0)
    .join(' - ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Toegestane tekens in een weergavenaam-patroon (literale tekst mag). */
const WEERGAVENAAM_PATROON_RE = /^[\p{L}\p{N} {}\-_.,()&/]+$/u

export const UpdateWeergavenaamPatroonSchema = z.object({
  documenttype: z.enum(DOCUMENT_TYPE),
  patroon: z
    .string()
    .trim()
    .min(1, 'Geef een patroon op')
    .max(200, 'Maximaal 200 tekens')
    .regex(WEERGAVENAAM_PATROON_RE, 'Ongeldige tekens in het patroon')
    .refine(
      (v) => {
        const m = v.match(WEERGAVENAAM_TOKEN_RE)
        return m !== null && m.length > 0
      },
      { message: 'Gebruik minstens één token, bv. {type}' },
    )
    .refine(
      (v) =>
        [...v.matchAll(WEERGAVENAAM_TOKEN_RE)].every((m) =>
          (WEERGAVENAAM_TOKENS as readonly string[]).includes(m[1]),
        ),
      { message: `Onbekende token; beschikbaar: ${WEERGAVENAAM_TOKENS.map((t) => `{${t}}`).join(', ')}` },
    ),
})
export type UpdateWeergavenaamPatroonInput = z.infer<typeof UpdateWeergavenaamPatroonSchema>

export interface WeergavenaamPatroonDTO {
  documenttype: DocumentType
  patroon: string
  /** true = geen opgeslagen rij; de ingebouwde default geldt. */
  isDefault: boolean
}

/** Voorbeeldwaarden voor het live-voorbeeld in de instellingen-UI. */
export const WEERGAVENAAM_PREVIEW_TOKENS: Record<WeergavenaamToken, string> = {
  type: 'Certificaat',
  betrokkene: 'J. de Vries',
  datum: '12-05-2026',
  bestandsnaam: 'Vca Basis 2026',
}

// ─── Mappad → label-voorstellen (FO-145: labels zijn de virtuele mappen) ──────

/** Slug een mapnaam naar een label-code (A-Z, 0-9, underscore; max 60). */
export function labelCodeVoorMapnaam(segment: string): string {
  return segment
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
}

/** Maximaal aantal mapsegmenten dat een label-voorstel wordt (diepe bomen cappen). */
export const MAP_LABEL_MAX_SEGMENTEN = 6

/**
 * Vertaal het relatieve mappad van een map-upload (zonder bestandsnaam, met
 * `/`-scheiding) naar label-voorstellen: elk mapsegment één label, zodat de
 * structuur als virtuele mappen (FO-145 labels-weergave) terugkomt.
 */
export function mapPadNaarLabelVoorstellen(
  relatiefPad: string,
): Array<{ code: string; label: string }> {
  const segmenten = relatiefPad
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, MAP_LABEL_MAX_SEGMENTEN)
  const uniek = new Map<string, string>()
  for (const segment of segmenten) {
    const code = labelCodeVoorMapnaam(segment)
    if (code.length === 0) continue
    if (!uniek.has(code)) uniek.set(code, segment)
  }
  return [...uniek.entries()].map(([code, label]) => ({ code, label }))
}

// ─── Request/response van de metadata-voorstel-action ────────────────────────

/**
 * Upload-context van het gedeelde formulier. `betrokkeneNaam` is de
 * weergavenaam voor het `{betrokkene}`-token (medewerker, relatie of kenteken);
 * scope/categorie voeden de label-engine-preview.
 */
export const uploadVoorstelContextSchema = z.object({
  soort: z.enum(UPLOAD_CONTEXT_SOORTEN),
  betrokkeneNaam: z.string().trim().max(120).optional(),
  scope: z.enum(DOCUMENT_SCOPE).optional(),
  categorie: z.enum(DOCUMENT_CATEGORIE).optional(),
})
export type UploadVoorstelContext = z.infer<typeof uploadVoorstelContextSchema>

export const stelDocumentMetadataVoorItemSchema = z.object({
  /** Client-key om het antwoord aan de rij te koppelen. */
  rijId: z.string().trim().min(1).max(80),
  bestandsnaam: z.string().trim().min(1).max(300),
  /** Mapstructuur uit een map-upload: directories, '/'-gescheiden, zonder bestandsnaam. */
  relatiefPad: z.string().trim().max(1000).optional(),
  /** Door de gebruiker gekozen type — wint altijd van de heuristiek. */
  gekozenType: z.enum(DOCUMENT_TYPE).optional(),
  /** Actuele tags in het formulier (voeden de label-engine-preview). */
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  /**
   * FO-375 — de reeds op de rij gekoppelde medewerker. De voorstel-route
   * resolvet daar de weergavenaam van voor het `{betrokkene}`-token, zodat een
   * (handmatig of via voorstel) gekoppelde medewerker de naam voedt.
   */
  gekoppeldeMedewerkerId: z.string().trim().min(1).max(80).optional(),
  /**
   * FO-375 — expliciete betrokkene-naam als override voor het `{betrokkene}`-
   * token (wint van de auto-match). Leeg = de route leidt de betrokkene af uit
   * `gekoppeldeMedewerkerId` of de naam-match.
   */
  betrokkeneNaam: z.string().trim().max(120).optional(),
})
export type StelDocumentMetadataVoorItem = z.infer<typeof stelDocumentMetadataVoorItemSchema>

export const stelDocumentMetadataVoorSchema = z.object({
  context: uploadVoorstelContextSchema,
  bestanden: z.array(stelDocumentMetadataVoorItemSchema).min(1).max(500),
})
export type StelDocumentMetadataVoorInput = z.infer<typeof stelDocumentMetadataVoorSchema>

export interface DocumentMetadataVoorstelDTO {
  rijId: string
  /** Naam-voorstel (patroon zodra type + betrokkene bekend, anders geschoonde bestandsnaam). */
  naam: string
  naamBron: 'patroon' | 'bestandsnaam'
  /** Type-voorstel; null = onzeker → "kies type"-nudge, nooit een stille gok. */
  type: DocumentType | null
  typeBron: 'gekozen' | 'sleutelwoord' | 'context' | 'ai' | null
  /** FO-145-engine-preview: exact de labels die opslaan zou koppelen. */
  labels: Array<{ code: string; label: string }>
  /** Label-voorstellen uit de mapstructuur (map-upload). */
  mapLabels: Array<{ code: string; label: string }>
  /**
   * FO-375 — medewerker-voorstel uit bestandsnaam/pad (alleen bibliotheek-
   * context). null/afwezig = geen matcher gedraaid of geen match.
   */
  medewerkerVoorstel?: MedewerkerVoorstelDTO | null
}

// ─── Duplicaat-check (SHA-256 inhoud-hash) ────────────────────────────────────

export const documentDuplicaatCheckSchema = z.object({
  items: z
    .array(
      z.object({
        rijId: z.string().trim().min(1).max(80),
        /** SHA-256 hex (lowercase) van de bestandsinhoud. */
        hash: z.string().regex(/^[0-9a-f]{64}$/, 'Ongeldige SHA-256-hash'),
      }),
    )
    .min(1)
    .max(500),
})
export type DocumentDuplicaatCheckInput = z.infer<typeof documentDuplicaatCheckSchema>

export interface DocumentDuplicaatMatchDTO {
  rijId: string
  documentId: string
  titel: string
  versionNr: number
  fileName: string
}

// ─── Expliciete label-set op het upload-pad (opslaan = exact wat getoond) ─────

/**
 * Labels-veld van POST /documenten (upload-pad): de exacte label-set uit het
 * formulier. `label` is de weergavenaam voor het geval de code nog niet in de
 * legenda bestaat (mappad-labels). Zonder dit veld geldt het bestaande
 * FO-145-gedrag (engine labelt bij aanmaak) — niet-upload-paden veranderen niet.
 */
export const uploadLabelsSchema = z
  .array(
    z.object({
      code: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .regex(/^[A-Z0-9_]+$/, 'Alleen hoofdletters, cijfers en underscores'),
      label: z.string().trim().min(1).max(120).optional(),
    }),
  )
  .max(30)
export type UploadLabelsInput = z.infer<typeof uploadLabelsSchema>

/** Herbruikbare weergave van het type-label voor het `{type}`-token. */
export function weergaveVoorDocumenttype(type: DocumentType): string {
  // Het schadeformulier-label bevat een toelichting tussen haakjes; voor een
  // documentnaam is de korte vorm beter.
  if (type === 'SCHADEFORMULIER') return 'Schadeformulier'
  // "Inzetverklaring voertuig - <kenteken> - <datum>" zou dubbelop zijn; de
  // betrokkene in de voertuig-context is al het kenteken.
  if (type === 'INZETVERKLARING') return 'Inzetverklaring'
  return BIBLIOTHEEK_DOCUMENT_TYPE_LABELS[type]
}

/**
 * Leid de (upload-)categorie af uit het bibliotheek-documenttype, zodat het
 * gedeelde upload-formulier één type-keuze heeft en de gebruiker niet ook nog
 * een aparte categorie hoeft in te vullen (productvisie: geen dubbele invoer).
 * De afleiding voedt óók de label-engine-preview — preview en opslag gebruiken
 * dus dezelfde categorie. Zonder type: OVERIG (bestaande default).
 */
export function categorieVoorDocumenttype(
  type: DocumentType | null | undefined,
): (typeof DOCUMENT_CATEGORIE)[number] {
  switch (type) {
    case 'CERTIFICAAT':
    case 'DIPLOMA':
    // batch-65 apk-rapport (FO-478, spec A1) - een keuringsbewijs.
    case 'APK_RAPPORT':
      return 'CERTIFICAAT'
    case 'ARBEIDSOVEREENKOMST':
      return 'CONTRACT'
    case 'BRUIKLEENOVEREENKOMST':
    case 'KLANTCONTRACT':
      return 'OVEREENKOMST'
    case 'HANDLEIDING':
      return 'HANDLEIDING'
    case 'VEILIGHEIDSDOCUMENT':
      return 'PROTOCOL'
    default:
      return 'OVERIG'
  }
}
